/* eslint-disable react/jsx-no-bind */
import React, { useState, useEffect, useImperativeHandle, useCallback } from 'react';
import { useSpring } from 'react-spring';
import { Button } from '../Button';
import { SettingsIcon } from '../Icons';
import { Loading } from '../Loading';
import * as Ui from './styles';

/**
 * Modal
 *
 * Obs: A responsabilidade de dimensionar corretamente a altura do 'modal-body'
 * fica por conta do componente filho.
 *
 */
function ModalReferenciable({
  // título do modal
  title = 'Título *',
  // oculta o título do modal
  hideTitle = false,
  // apresenta os botões no modal
  showActions = true,
  // oculta o botão OK
  hiddenOkButton = false,
  // define o nome do botão OK
  okBtnTitle = 'Ok',
  // define o nome do botão Cancel
  cancelBtnTitle = 'Cancelar',
  // define a largura do modal (String)
  width,
  // define o tipo de display do body do modal (String)
  bodyDisplayType,
  // define a altura do body do modal(String)
  contentHeight,
  // define a borda do title
  borderTitle,
  // define o peso da fonte do titulo
  fontWeightTitle,
  // apresenta o modal
  isOpen = false,
  // adiciona um load no corpo do modal
  isLoading = false,
  // ícone do título
  iconComponent,
  // propaga o evento de acionamento do botão Cancel
  onCancel,
  // propaga o evento de acionamento do botão OK
  onOk,
  // propaga o evento de seleção de um tab
  onSelectTab,
  // define os tabs que deverão existir
  tabs = [],
  // define if loadSpinner will be showed
  withLoadingSpinner = false,
  //
  footerContent,
  // posição dos botoes
  btnPosition = 'center',
  // outras propriedades
  ...props
}, ref) {
  // others
  const { children, className } = props;
  // state
  const [modalIsVisible, setVisibilityModal] = useState(isOpen);
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [headerIcon, setHeaderIcon] = useState();
  const FooterContent = footerContent;

  // animation
  const animationProps = useSpring({
    from: {
      transform: isOpen ? 'translate3d(100%, 0, 0)' : 'translate3d(0%, 0, 0)',
      opacity: isOpen ? 0 : 1,
    },
    to: {
      transform: isOpen ? 'translate3d(0%, 0, 0' : 'translate3d(100%, 0, 0)',
      opacity: isOpen ? 1 : 0,
    },
    onStart: () => {
      if (isOpen) setVisibilityModal(true);
    },
    onRest: () => {
      if (!isOpen) setVisibilityModal(false);
    },
  });

  // api para este componente poder
  // ser acesso pelo parent
  useImperativeHandle(ref, () => ({
    selectTab: (index) => {
      setSelectedTabIndex(index);
    },
  }));

  // callbacks
  // propaga o evento de onClose para caso de pressionar ESC
  const handleExitByDOMEvent = useCallback((event) => {
    if (event.keyCode === 27) {
      if (onCancel) onCancel();
    }
  }, [onCancel]);

  // efeito obtido para pressionar a tecla ESC
  useEffect(() => {
    document.addEventListener('keydown', handleExitByDOMEvent, false);
    return () => {
      document.removeEventListener('keydown', handleExitByDOMEvent, false);
    };
  });

  /**
   * Callback executado para definir o icone de exibicao
   * no cabecalho
   */
  useEffect(() => {
    if (!iconComponent) {
      setHeaderIcon(<SettingsIcon />);
    } else {
      setHeaderIcon(iconComponent);
    }
  }, [iconComponent]);

  // handle reset state
  function handleCancel() {
    if (onCancel) onCancel();
  }

  // handle accepted file
  function handleOk() {
    if (onOk) onOk();
  }

  // renders: loading
  function renderLoadingSpinner() {
    return (
      <Ui.ItemsEmpty>
        <Loading size={30} running={isLoading} />
      </Ui.ItemsEmpty>
    );
  }
  // handle on click in tab by index.
  // to reverse this selection, set onSelectTab(index, reverseFunc) in parent
  // and perform reverseFunc(true);
  function handleSelectTab(index) {
    if (selectedTabIndex !== index) {
      const tmpIndex = selectedTabIndex;
      setSelectedTabIndex(index);
      if (onSelectTab) {
        onSelectTab(index, () => {
          setTimeout(() => {
            setSelectedTabIndex(tmpIndex);
          }, 100);
        });
      }
    }
  }

  // render tabs when exists
  function renderTabs() {
    return (
      <Ui.Tabs>
        {tabs.map((name, index) => (
          <Ui.Tab key={`key-${name}`} className={selectedTabIndex === index ? 'active' : null}>
            <Ui.TabAction clear onClick={() => handleSelectTab(index)}>
              {name}
            </Ui.TabAction>
          </Ui.Tab>
        ))}
      </Ui.Tabs>
    );
  }

  // conditional render
  if (!isOpen) {
    return null;
  }

  return (
    <Ui.Container style={{ ...animationProps, visibility: modalIsVisible ? 'visible' : 'hidden' }}>
      <Ui.Panel className={className} width={width}>
        <Ui.Header hide={hideTitle} borderTitle={borderTitle}>
          <Ui.Title fontWeightTitle={fontWeightTitle}>
            <div className="icon-wrapper">{headerIcon}</div>
            &nbsp;
            {title}
          </Ui.Title>
          {renderTabs()}
        </Ui.Header>
        <Ui.Body className="modal-body" contentHeight={contentHeight} bodyDisplayType={bodyDisplayType}>
          { isLoading && withLoadingSpinner ? renderLoadingSpinner() : children}
        </Ui.Body>
        {FooterContent ? <FooterContent /> : ''}
        {showActions && (
          <Ui.Actions btnPosition={btnPosition}>
            <Button
              type="button"
              title={cancelBtnTitle}
              loading={isLoading}
              onClick={() => handleCancel()}
              clear
              className="cancelModalBtn"
            />
            {!isLoading && !hiddenOkButton ? (
              <Button
                type="button"
                title={okBtnTitle}
                loading={isLoading}
                onClick={() => handleOk()}
                className="okModalBtn"
                clickable={props.okBtnClickable}
              />
            ) : null}
          </Ui.Actions>
        )}
      </Ui.Panel>
    </Ui.Container>
  );
}

const Modal = React.forwardRef(ModalReferenciable);

export { Modal };
